import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { apiErrorMessage } from '../api/client';
import {
  describeRequestSummary,
  formatLogTime,
  getErrors,
  getHourlyStats,
  getLogSummary,
  getRequests,
  getTopUsers,
  parseLogTimestamp,
} from '../api/logsApi';
import { Card, EmptyState, ErrorBanner, LoadMore, Spinner, TableSkeleton } from '../components/ui';
import { IconAlert, IconChevronDown, IconEye } from '../components/icons';
import { MethodBadge, SessionModal, StatusBadge } from '../components/SessionTimeline';
import { useTheme } from '../context/ThemeContext';
import { chartTheme } from '../utils/chartTheme';
import {
  EndpointSummary,
  HourlyStat,
  LogRange,
  LogSummary,
  RequestLog,
  StatusFilter,
  TopUser,
} from '../types/logs';

/* ─────────────────────────── constants & helpers ─────────────────────────── */

const RANGES: { value: LogRange; label: string }[] = [
  { value: '24h', label: '24h' },
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
];

const AUTO_REFRESH_MS = 30_000;
const PAGE_SIZE = 50;

// Green above 95%, yellow above 90%, red below — the thresholds an on-call
// glance actually cares about.
function successRateTone(rate: number): string {
  if (rate > 95) return 'text-ok';
  if (rate > 90) return 'text-warn';
  return 'text-bad';
}

function formatMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}

function formatCount(n: number | null | undefined): string {
  return (n ?? 0).toLocaleString('en-IN');
}

/** A user_id worth linking to — anonymous traffic has none. */
function isRealUser(id: string | null): id is string {
  return !!id && id !== 'anonymous';
}

function UserCell({ userId }: { userId: string | null }) {
  if (!isRealUser(userId)) return <span className="text-sub/70">anonymous</span>;
  return (
    // /users/:id, not /users?id=… — UsersPage only reads q/page/purchased/range,
    // so an `id` query param would silently land on the unfiltered list. This is
    // the same link every other page in the panel uses to open one user.
    <Link
      to={`/users/${encodeURIComponent(userId)}`}
      className="font-mono text-xs text-gold underline-offset-2 hover:underline"
      title={userId}
    >
      {userId.length > 14 ? `${userId.slice(0, 14)}…` : userId}
    </Link>
  );
}

/* ─────────────────────────────── summary cards ───────────────────────────── */

function StatCard({
  label,
  value,
  tone = 'text-ink',
  hint,
}: {
  label: string;
  value: string;
  tone?: string;
  hint?: string;
}) {
  return (
    <div className="card p-4">
      <p className="text-xs font-medium uppercase tracking-wide text-sub">{label}</p>
      <p className={`mt-1.5 text-2xl font-semibold tabular-nums ${tone}`}>{value}</p>
      {hint && <p className="mt-0.5 text-xs text-sub/70">{hint}</p>}
    </div>
  );
}

/* ──────────────────────────── call volume chart ──────────────────────────── */

function VolumeChart({ hourly }: { hourly: HourlyStat[] }) {
  const { isDark } = useTheme();
  const t = chartTheme(isDark);

  if (hourly.length === 0) {
    return <EmptyState message="No traffic in the last 24 hours" />;
  }

  return (
    <div className="h-[200px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={hourly} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
          <CartesianGrid stroke={t.grid} strokeDasharray="3 3" vertical={false} />
          <XAxis
            dataKey="hour"
            // The bucket is "YYYY-MM-DD HH:00:00" UTC; show it as the viewer's
            // local hour so it lines up with the clock on the wall.
            tickFormatter={(v: string) => {
              const d = parseLogTimestamp(v);
              return d ? `${String(d.getHours()).padStart(2, '0')}:00` : v;
            }}
            tick={{ fill: t.axis, fontSize: 11 }}
            stroke={t.grid}
            tickLine={false}
            minTickGap={16}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fill: t.axis, fontSize: 11 }}
            stroke={t.grid}
            tickLine={false}
            axisLine={false}
            width={36}
          />
          <Tooltip
            contentStyle={t.tooltip}
            labelFormatter={(l: any) => formatLogTime(String(l))}
            formatter={(value: any, name: any) => [formatCount(Number(value)), name]}
          />
          {/* Labelled directly below rather than with a legend — two series do
              not need a key taking up chart height. */}
          <Line
            type="monotone"
            dataKey="calls"
            name="Total calls"
            stroke={t.gold}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
          <Line
            type="monotone"
            dataKey="errors"
            name="Errors"
            stroke="#ef4444"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/* ─────────────────────── request detail (inline expand) ──────────────────── */

/** One log row, expandable to show the real path and the error text. */
function RequestRow({ log }: { log: RequestLog }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr
        onClick={() => setOpen((v) => !v)}
        className="cursor-pointer border-t border-edge/60 transition-colors hover:bg-edge/20"
      >
        <td className="td text-xs text-sub">{formatLogTime(log.timestamp)}</td>
        <td className="td">
          <StatusBadge status={log.status} />
        </td>
        <td className="td tabular-nums text-xs">{formatMs(log.duration_ms)}</td>
        <td className="td text-xs">
          <UserCell userId={log.user_id} />
        </td>
        <td className="td font-mono text-xs text-sub">{log.ip ?? '—'}</td>
        <td className="td font-mono text-[11px] text-sub/80" title={log.request_id ?? ''}>
          {log.request_id ? `${log.request_id.slice(0, 8)}…` : '—'}
        </td>
      </tr>
      {open && (
        <tr className="border-t border-edge/60 bg-app/40">
          <td colSpan={6} className="px-4 py-3">
            <dl className="space-y-1.5 text-xs">
              <div className="flex gap-2">
                <dt className="w-20 shrink-0 font-medium text-sub">Full path</dt>
                <dd className="break-all font-mono text-ink">{log.path}</dd>
              </div>
              {log.status >= 400 && log.error && (
                <div className="flex gap-2">
                  <dt className="w-20 shrink-0 font-medium text-sub">Error</dt>
                  <dd className="break-all font-mono text-bad">{log.error}</dd>
                </div>
              )}
              <div className="flex gap-2">
                <dt className="w-20 shrink-0 font-medium text-sub">Request ID</dt>
                <dd className="break-all font-mono text-sub">{log.request_id ?? '—'}</dd>
              </div>
            </dl>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * The panel that opens under an endpoint row.
 *
 * Deliberately NOT wired to auto-refresh: it is opened to read a specific set of
 * requests, and having the rows shuffle under the cursor every 30 seconds makes
 * that impossible.
 */
function RequestDetailPanel({ endpoint, range }: { endpoint: EndpointSummary; range: LogRange }) {
  const [tab, setTab] = useState<StatusFilter>('all');
  const [logs, setLogs] = useState<RequestLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(
    async (nextPage: number, replace: boolean) => {
      setLoading(true);
      setError('');
      try {
        const res = await getRequests({
          path: endpoint.path_normalized,
          // Filtered server-side on method as well as path, so `total` matches
          // the Calls count on the row that was clicked.
          method: endpoint.method,
          status: tab,
          range,
          page: nextPage,
          limit: PAGE_SIZE,
        });
        setLogs((prev) => (replace ? res.logs : [...prev, ...res.logs]));
        setTotal(res.pagination.total);
        setPage(nextPage);
      } catch (err) {
        setError(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [endpoint.path_normalized, endpoint.method, tab, range]
  );

  useEffect(() => {
    load(1, true);
  }, [load]);

  return (
    <div className="space-y-3 border-t border-edge bg-app/40 px-4 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold">
          <MethodBadge method={endpoint.method} />
          <span className="font-mono">{endpoint.path_normalized}</span>
        </h3>
        <div className="flex gap-1 rounded-lg border border-edge bg-surface p-0.5">
          {(['all', 'error'] as StatusFilter[]).map((value) => (
            <button
              key={value}
              onClick={() => setTab(value)}
              className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                tab === value ? 'bg-gold text-[#1a1408]' : 'text-sub hover:text-ink'
              }`}
            >
              {value === 'all' ? 'All' : 'Errors Only'}
            </button>
          ))}
        </div>
      </div>

      {error && <ErrorBanner message={error} onRetry={() => load(1, true)} />}

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-edge/20">
                <th className="th">Timestamp</th>
                <th className="th">Status</th>
                <th className="th">Duration</th>
                <th className="th">User</th>
                <th className="th">IP</th>
                <th className="th">Request ID</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <RequestRow key={log.id} log={log} />
              ))}
            </tbody>
          </table>
        </div>

        {loading && logs.length === 0 && <TableSkeleton rows={3} />}
        {!loading && logs.length === 0 && !error && (
          <EmptyState message={tab === 'error' ? 'No errors on this endpoint' : 'No requests found'} />
        )}
        <LoadMore
          shown={logs.length}
          total={total}
          loading={loading}
          onMore={() => load(page + 1, false)}
        />
      </div>
      <p className="text-xs text-sub/70">
        Click any row to see the real path it was called with.
      </p>
    </div>
  );
}

/* ───────────────────────────── endpoints table ───────────────────────────── */

type SortKey = 'total_calls' | 'success_rate' | 'avg_duration_ms';

function EndpointsTable({
  endpoints,
  loading,
  range,
}: {
  endpoints: EndpointSummary[];
  loading: boolean;
  range: LogRange;
}) {
  const [sortKey, setSortKey] = useState<SortKey>('total_calls');
  const [asc, setAsc] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const sorted = useMemo(() => {
    const rows = [...endpoints];
    rows.sort((a, b) => (asc ? a[sortKey] - b[sortKey] : b[sortKey] - a[sortKey]));
    return rows;
  }, [endpoints, sortKey, asc]);

  const toggleSort = (key: SortKey) => {
    if (key === sortKey) setAsc((v) => !v);
    else {
      setSortKey(key);
      setAsc(false);
    }
  };

  const SortHeader = ({ label, sortBy }: { label: string; sortBy: SortKey }) => (
    <th className="th">
      <button
        onClick={() => toggleSort(sortBy)}
        className="inline-flex items-center gap-1 uppercase tracking-wider transition-colors hover:text-ink"
      >
        {label}
        <span className={sortKey === sortBy ? 'text-gold' : 'text-sub/40'}>
          {sortKey === sortBy && asc ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );

  if (loading && endpoints.length === 0) return <TableSkeleton rows={6} />;
  if (endpoints.length === 0) {
    return (
      <EmptyState
        message="No requests in this period"
        hint="Only customer traffic is recorded — admin routes are excluded by design."
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-edge">
            <th className="th">Method</th>
            <th className="th">Endpoint</th>
            <SortHeader label="Calls" sortBy="total_calls" />
            <SortHeader label="Success Rate" sortBy="success_rate" />
            <SortHeader label="Avg Time" sortBy="avg_duration_ms" />
            <th className="th">Max Time</th>
            <th className="th">Errors</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((ep) => {
            const key = `${ep.method} ${ep.path_normalized}`;
            const isOpen = expanded === key;
            return (
              <React.Fragment key={key}>
                <tr
                  onClick={() => setExpanded(isOpen ? null : key)}
                  className={`cursor-pointer border-b border-edge/60 transition-colors hover:bg-edge/20 ${
                    isOpen ? 'bg-edge/20' : ''
                  }`}
                >
                  <td className="td">
                    <MethodBadge method={ep.method} />
                  </td>
                  <td className="td">
                    <span className="flex items-center gap-1.5 font-mono text-xs">
                      <IconChevronDown
                        className={`h-3.5 w-3.5 shrink-0 text-sub transition-transform ${
                          isOpen ? 'rotate-180' : ''
                        }`}
                      />
                      {ep.path_normalized}
                    </span>
                  </td>
                  <td className="td tabular-nums">{formatCount(ep.total_calls)}</td>
                  <td className="td">
                    <div className="flex items-center gap-2">
                      {/* Inline bar: the number alone makes 91% and 99% look
                          alike at a glance; the fill does not. */}
                      <div className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-edge">
                        <div
                          className="h-full rounded-full bg-ok"
                          style={{ width: `${Math.max(0, Math.min(100, ep.success_rate))}%` }}
                        />
                      </div>
                      <span className={`tabular-nums text-xs ${successRateTone(ep.success_rate)}`}>
                        {ep.success_rate}%
                      </span>
                    </div>
                  </td>
                  <td className="td tabular-nums text-xs">{formatMs(ep.avg_duration_ms)}</td>
                  <td className="td tabular-nums text-xs text-sub">{formatMs(ep.max_duration_ms)}</td>
                  <td className={`td tabular-nums ${ep.error_count > 0 ? 'text-bad' : 'text-sub'}`}>
                    {formatCount(ep.error_count)}
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={7} className="p-0">
                      <RequestDetailPanel endpoint={ep} range={range} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ────────────────────────────── errors tab ──────────────────────────────── */

function ErrorRow({ log, onViewSession }: { log: RequestLog; onViewSession: (id: string) => void }) {
  // response_error carries the actual thrown message where one exists; `error`
  // is only the "<METHOD> <path> returned <status>" marker, which the Status and
  // Path columns already say.
  const message = log.response_error || log.error;
  const summary = describeRequestSummary(log.request_summary);

  return (
    <tr className="border-t border-edge/60 transition-colors hover:bg-bad/5">
      <td className="td text-xs text-sub">{formatLogTime(log.timestamp)}</td>
      <td className="td">
        <MethodBadge method={log.method} />
      </td>
      <td className="td max-w-[22rem] truncate font-mono text-xs" title={log.path}>
        {log.path}
      </td>
      <td className="td">
        <StatusBadge status={log.status} />
      </td>
      <td className="td text-xs">
        <UserCell userId={log.user_id} />
      </td>
      <td className="td max-w-[18rem] text-xs text-bad">
        <span className="block truncate" title={message ?? ''}>
          {message ?? '—'}
        </span>
        {/* Key names only — never the values that were sent. */}
        {summary && (
          <span className="block truncate text-[11px] text-sub/70" title={summary}>
            {summary}
          </span>
        )}
      </td>
      <td className="td">
        {log.session_id ? (
          <button
            onClick={() => onViewSession(log.session_id as string)}
            className="btn-ghost !px-2 !py-1 !text-xs"
            title="What else happened in this session"
          >
            <IconEye className="h-3.5 w-3.5" />
            Session
          </button>
        ) : (
          <span className="text-xs text-sub/50">—</span>
        )}
      </td>
    </tr>
  );
}

function ErrorsTab({ range, refreshKey }: { range: LogRange; refreshKey: number }) {
  // Which session's timeline is open, if any. Kept here rather than per-row so
  // only one modal can ever be mounted.
  const [openSession, setOpenSession] = useState<string | null>(null);
  const [logs, setLogs] = useState<RequestLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(
    async (nextPage: number, replace: boolean) => {
      setLoading(true);
      setError('');
      try {
        const res = await getErrors({ range, page: nextPage, limit: PAGE_SIZE });
        setLogs((prev) => (replace ? res.logs : [...prev, ...res.logs]));
        setTotal(res.pagination.total);
        setPage(nextPage);
      } catch (err) {
        setError(apiErrorMessage(err));
      } finally {
        setLoading(false);
      }
    },
    [range]
  );

  // refreshKey is in the dep list so auto-refresh reloads the feed from page 1 —
  // this is the "what is broken right now" view, so newest-first matters more
  // than holding the reader's scroll position.
  useEffect(() => {
    load(1, true);
  }, [load, refreshKey]);

  return (
    <Card
      className="border-bad/30"
      title="Errors — all 4xx and 5xx"
      action={
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-bad">
          <IconAlert className="h-4 w-4" />
          {formatCount(total)} in this period
        </span>
      }
      bodyClassName="!p-0"
    >
      {error && (
        <div className="p-4">
          <ErrorBanner message={error} onRetry={() => load(1, true)} />
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-bad/5">
              <th className="th">Timestamp</th>
              <th className="th">Method</th>
              <th className="th">Path</th>
              <th className="th">Status</th>
              <th className="th">User</th>
              <th className="th">Error</th>
              <th className="th">Context</th>
            </tr>
          </thead>
          <tbody>
            {logs.map((log) => (
              <ErrorRow key={log.id} log={log} onViewSession={setOpenSession} />
            ))}
          </tbody>
        </table>
      </div>

      {loading && logs.length === 0 && <TableSkeleton rows={5} />}
      {!loading && logs.length === 0 && !error && (
        <EmptyState message="No errors in this period" hint="Nothing is broken right now." />
      )}
      <LoadMore shown={logs.length} total={total} loading={loading} onMore={() => load(page + 1, false)} />

      <SessionModal sessionId={openSession} onClose={() => setOpenSession(null)} />
    </Card>
  );
}

/* ───────────────────────────── top users section ─────────────────────────── */

function TopUsersSection({ refreshKey }: { refreshKey: number }) {
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState<TopUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Only fetched once opened — a collapsed section should not cost a request on
  // every page load or auto-refresh tick.
  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    getTopUsers()
      .then((rows) => alive && setUsers(rows.slice(0, 20)))
      .catch((err) => alive && setError(apiErrorMessage(err)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [open, refreshKey]);

  return (
    <div className="card overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left transition-colors hover:bg-edge/20"
      >
        <span className="text-sm font-semibold">Top Users (last 7 days)</span>
        <IconChevronDown
          className={`h-4 w-4 text-sub transition-transform ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div className="border-t border-edge">
          {error && (
            <div className="p-4">
              <ErrorBanner message={error} />
            </div>
          )}
          {loading && users.length === 0 && <TableSkeleton rows={4} />}
          {!loading && users.length === 0 && !error && (
            <EmptyState message="No signed-in traffic in the last 7 days" />
          )}
          {users.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-edge/20">
                    <th className="th">User ID</th>
                    <th className="th">Requests</th>
                    <th className="th">Errors</th>
                    <th className="th">First Seen</th>
                    <th className="th">Last Active</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.user_id} className="border-t border-edge/60 hover:bg-edge/20">
                      <td className="td">
                        <UserCell userId={u.user_id} />
                      </td>
                      <td className="td tabular-nums">{formatCount(u.total_calls)}</td>
                      <td className={`td tabular-nums ${u.errors > 0 ? 'text-bad' : 'text-sub'}`}>
                        {formatCount(u.errors)}
                      </td>
                      <td className="td text-xs text-sub">{formatLogTime(u.first_seen)}</td>
                      <td className="td text-xs text-sub">{formatLogTime(u.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────── page ────────────────────────────────── */

export default function ApiLogsPage() {
  const [range, setRange] = useState<LogRange>('7d');
  const [tab, setTab] = useState<'endpoints' | 'errors'>('endpoints');
  const [autoRefresh, setAutoRefresh] = useState(false);

  const [summary, setSummary] = useState<LogSummary | null>(null);
  const [hourly, setHourly] = useState<HourlyStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Bumped on every (re)fetch. Child feeds watch it to reload in step with the
  // header, instead of each keeping its own timer.
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, h] = await Promise.all([getLogSummary(range), getHourlyStats()]);
      setSummary(s);
      setHourly(h);
      setLastUpdated(new Date());
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto-refresh, off by default. Re-fetches summary + stats (and, via
  // refreshKey, the errors feed) — never an open request-detail panel.
  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(load, AUTO_REFRESH_MS);
    return () => clearInterval(id);
  }, [autoRefresh, load]);

  // Re-render once a second purely so "Last updated: Ns ago" counts up. Cheap,
  // and only while a timestamp exists to describe.
  const hasTimestamp = !!lastUpdated;
  useEffect(() => {
    if (!hasTimestamp) return;
    const id = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [hasTimestamp]);

  const secondsAgo = lastUpdated
    ? Math.max(0, Math.round((Date.now() - lastUpdated.getTime()) / 1000))
    : null;

  const totals = summary?.totals;
  const rate = totals?.overall_success_rate ?? 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">API Analytics</h1>
          <p className="mt-0.5 text-sm text-sub">User traffic only · Admin routes excluded</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 rounded-xl border border-edge bg-surface p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setRange(r.value)}
                className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  range === r.value ? 'bg-gold text-[#1a1408]' : 'text-sub hover:text-ink'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>

          <button onClick={load} disabled={loading} className="btn-outline !px-3 !py-1.5 !text-xs">
            {loading ? <Spinner /> : <span aria-hidden="true">↻</span>}
            Refresh
          </button>

          <label className="flex cursor-pointer select-none items-center gap-2 rounded-xl border border-edge bg-surface px-3 py-1.5 text-xs text-sub">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="h-3.5 w-3.5 accent-[rgb(var(--c-accent))]"
            />
            Auto-refresh 30s
          </label>
        </div>
      </div>

      {secondsAgo !== null && (
        <p className="-mt-2 text-xs text-sub/70">
          Last updated: {secondsAgo === 0 ? 'just now' : `${secondsAgo}s ago`}
        </p>
      )}

      {error && <ErrorBanner message={error} onRetry={load} />}

      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Total Requests" value={formatCount(totals?.total_calls)} tone="text-gold" />
        <StatCard label="Success Rate" value={`${rate}%`} tone={successRateTone(rate)} />
        <StatCard label="Avg Response Time" value={formatMs(totals?.avg_duration_ms)} />
        <StatCard
          label="Total Errors"
          value={formatCount(totals?.total_errors)}
          tone={(totals?.total_errors ?? 0) > 0 ? 'text-bad' : 'text-ink'}
        />
      </div>

      {/* Call volume */}
      <Card title="Call volume — last 24 hours">
        <VolumeChart hourly={hourly} />
        <div className="mt-3 flex items-center gap-4 border-t border-edge pt-3 text-xs text-sub">
          <span className="flex items-center gap-1.5">
            <span className="h-0.5 w-4 rounded-full bg-gold" aria-hidden="true" />
            Total calls
          </span>
          <span className="flex items-center gap-1.5">
            <span className="h-0.5 w-4 rounded-full" style={{ background: '#ef4444' }} aria-hidden="true" />
            Errors
          </span>
        </div>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 rounded-xl border border-edge bg-surface p-1">
        {([
          ['endpoints', 'Endpoints'],
          ['errors', 'Errors'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors sm:flex-none sm:px-5 ${
              tab === value
                ? value === 'errors'
                  ? 'bg-bad text-white'
                  : 'bg-gold text-[#1a1408]'
                : 'text-sub hover:text-ink'
            }`}
          >
            {label}
            {value === 'errors' && (totals?.total_errors ?? 0) > 0 && tab !== 'errors' && (
              <span className="ml-1.5 rounded-full bg-bad px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {formatCount(totals?.total_errors)}
              </span>
            )}
          </button>
        ))}
      </div>

      {tab === 'endpoints' ? (
        <>
          <Card title="Endpoints" bodyClassName="!p-0">
            <EndpointsTable
              endpoints={summary?.endpoints ?? []}
              loading={loading}
              range={range}
            />
          </Card>
          <TopUsersSection refreshKey={refreshKey} />
        </>
      ) : (
        <ErrorsTab range={range} refreshKey={refreshKey} />
      )}
    </div>
  );
}
